import { useEffect, useState } from "react";
import * as Y from "yjs";
import { type DomainMaps, LOCAL_ORIGIN } from "@electro-planner/domain";

export type UndoState = {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

/**
 * Y.UndoManager scopad till LOCAL_ORIGIN — Ctrl+Z ångrar BARA våra egna
 * mutationer, aldrig updates som kommer in via Hocuspocus (Fas 4+).
 *
 * captureTimeout grupperar rapida mutationer till en enda undo-step,
 * så att en drag-rörelse inte splittras i 50 micro-operations.
 */
export function useUndoManager(maps: DomainMaps): UndoState {
  const [mgr] = useState(
    () =>
      new Y.UndoManager(
        [
          maps.devices,
          maps.terminals,
          maps.conductors,
          maps.circuits,
          maps.cables,
          maps.sheets,
          maps.sheetOrder,
          maps.deviceLayout,
          maps.conductorRouting,
          maps.references,
          maps.projectMeta,
        ],
        {
          trackedOrigins: new Set([LOCAL_ORIGIN]),
          captureTimeout: 300,
        },
      ),
  );

  // Version bumpas vid stack-ändring så React re-renderar knapptillstånd.
  const [, setVersion] = useState(0);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    mgr.on("stack-item-added", bump);
    mgr.on("stack-item-popped", bump);
    mgr.on("stack-cleared", bump);
    return () => {
      mgr.off("stack-item-added", bump);
      mgr.off("stack-item-popped", bump);
      mgr.off("stack-cleared", bump);
      mgr.destroy();
    };
  }, [mgr]);

  return {
    undo: () => mgr.undo(),
    redo: () => mgr.redo(),
    canUndo: mgr.undoStack.length > 0,
    canRedo: mgr.redoStack.length > 0,
  };
}
